import React, { useState, useEffect, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { startSetRecommendedOffers } from '../../actions/offers';
import OwlCarousel from '../../components/OwlCarousel/OwlCarousel';
import Card from '../../components/Card/Card';
import OffersWidgetItem from './../../components/Offer/OffersWidgetItem';
import { chunk } from '../../utils/utils';

const ITEMS_PER_PAGE = 4;

const owlConfig = {
  className: 'owl-theme owl-footer-dots',
  items: 1,
  loop: false,
  nav: false,
  margin: 0,
  lazyLoad: true,
};

const RecommendedOffersWidget = ({ collapsing, localStorageName }) => {
  const dispatch = useDispatch();
  const recommendedOffers = useSelector((state) => state.offers.recommendedOffers);
  const [isLoading, setIsLoading] = useState(true);
  const [paginatedOffers, setPaginatedOffers] = useState(null);
  const [isWideLayout, setIsWideLayout] = useState(false);
  const contentRef = useRef(null);
  const shouldShowContent = !isLoading && paginatedOffers && paginatedOffers.length > 0;

  useEffect(() => {
    if (recommendedOffers?.length > 0) {
      const paginated = chunk(recommendedOffers, ITEMS_PER_PAGE);
      setPaginatedOffers(paginated);
    } else {
      setPaginatedOffers(null);
    }
  }, [recommendedOffers]);

  useEffect(() => {
    dispatch(startSetRecommendedOffers()).finally(() => setIsLoading(false));
  }, [dispatch]);

  useEffect(() => {
    const checkWidth = () => {
      if (contentRef.current) {
        const contentWidth = contentRef.current.offsetWidth;
        setIsWideLayout(contentWidth > 550);
      }
    };

    const mutationObserver = new MutationObserver(() => {
      checkWidth();
    });

    if (contentRef.current) {
      mutationObserver.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class', 'style'],
      });
    }

    checkWidth();

    return () => {
      mutationObserver.disconnect();
    };
  }, []);

  const renderOffersPages = (offersPage) => {
    if (!offersPage) return null;

    return offersPage.map((offer) =>
      offer ? (
        <OffersWidgetItem key={offer.id} payout={offer.short_pixels || offer.payout} {...offer} />
      ) : null,
    );
  };

  const renderOffersList = () => {
    return (
      <div className="dashboard-offers-widget-list">
        {recommendedOffers.map((offer) => (
          <OffersWidgetItem key={offer.id} payout={offer.short_pixels || offer.payout} {...offer} />
        ))}
      </div>
    );
  };

  return (
    <Card
      className="dashboard-offers-widget"
      localStorageName={localStorageName}
      collapsing={collapsing}
      loading={isLoading}
    >
      <Card.Header>
        <p className="dashboard-offers-widget-title">
          <FormattedMessage
            id="widgets.recommended-offers.title"
            defaultMessage="Recommended offers"
          />
        </p>
      </Card.Header>

      <Card.BodyNew>
        <div ref={contentRef}>
          {shouldShowContent &&
            (isWideLayout ? (
              <OwlCarousel {...owlConfig}>
                {paginatedOffers.map((page, index) => (
                  <div key={index} className="dashboard-offers-widget-page">
                    {renderOffersPages(page)}
                  </div>
                ))}
              </OwlCarousel>
            ) : (
              renderOffersList()
            ))}
        </div>
      </Card.BodyNew>
    </Card>
  );
};

export default RecommendedOffersWidget;
